import { OrderUpdate } from './booking-types.types'

interface Ordered {
  id: number
  order: number
}

export const sortByOrder = <T extends Ordered>(items: T[]) =>
  [...items].sort((a, b) => a.order - b.order || a.id - b.id)

export const applyOrderUpdates = <T extends Ordered>(items: T[], updates: OrderUpdate[]) => {
  const orderById = new Map(updates.map(({ id, order }) => [id, order]))
  return items.map((item) =>
    orderById.has(item.id) ? { ...item, order: orderById.get(item.id) as number } : item,
  )
}

// Note: gives the moved item prev.order + 1 and bumps only the following items it collides with,
// so legacy lists with duplicate or tightly packed orders stay stable
export const computeCascadeOrder = <T extends Ordered>(
  reorderedList: T[],
  movedIndex: number,
): OrderUpdate[] => {
  const moved = reorderedList[movedIndex]
  const prev = reorderedList[movedIndex - 1]
  const next = reorderedList[movedIndex + 1]

  let order = 1
  if (prev) order = prev.order + 1
  else if (next) order = next.order - 1

  const updates: OrderUpdate[] = [{ id: moved.id, order }]
  let previousOrder = order
  for (let i = movedIndex + 1; i < reorderedList.length; i++) {
    const item = reorderedList[i]
    if (item.order > previousOrder) break
    previousOrder += 1
    updates.push({ id: item.id, order: previousOrder })
  }
  return updates
}

export const moveItem = <T>(items: T[], fromIndex: number, toIndex: number) => {
  const next = [...items]
  const [moved] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, moved)
  return next
}

export const getNextOrder = (items: Ordered[]) =>
  items.length ? Math.max(...items.map(({ order }) => order)) + 1 : 1
